import Vue, { CreateElement } from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'
import ClickOutside from 'vue-click-outside'
import async from 'async'

import { Subtitle1 } from '@src/elements/typography/typography'
import { Button } from '@src/elements/button/button'
import { OptionGroup } from '@src/elements/select/option-group'
import { Select } from '@src/elements/select/select'
import { Option } from '@src/elements/select/option'
import ApiClient from '@src/lib/api_client'
import { HealthGoal, HealthIndicator } from '@src/data/goal'
import { API } from '@src/lib/api_path'
import { snakecaseKeys } from '@src/util/functions'
import { Modal } from '@src/elements/modal/modal'
import { Textarea } from '@src/elements/input/textarea'
import EventBus from '@src/lib/event_bus'

import './add-goal.sass'

type HealthGoalResponse = { health_goal: HealthGoal }

@Component({
  directives: {
    ClickOutside
  },
  computed: {
    goal () {
      return this.$store.state.goal
    },
    defaultGoals () {
      return this.$store.state.predefinedHealthGoals || []
    }
  }
})
export default class AddGoal extends Vue {

  @Prop({ required: false })
  protected closeCallback: () => void

  @Prop({ required: false, default: false })
  protected saveOnChange: boolean

  protected goal: HealthGoal | null
  protected currentGoal: HealthGoal = new HealthGoal()
  protected apiClient: ApiClient
  protected defaultGoals: HealthGoal[]
  protected saveInProgress: boolean = false

  constructor () {
    super()
    this.apiClient = new ApiClient()
  }

  save () {
    if (!this.saveInProgress) this.saveInProgress = true
    async.waterfall([
      this.removeIndicators,
      this.saveGoal,
      this.saveIndicators,
      this.loadCurrentGoal
    ], () => {
      EventBus.trigger('reloadHealthGoal')
      EventBus.trigger('reloadSelectedMember')
      this.close()
      this.saveInProgress = false
    })
  }

  saveGoal (done) {
    if (this.currentGoal.name || this.currentGoal.key) {
      // submit data
      return this.apiClient.put(API.TRAINERS_API.HEALTH_GOAL.replace('{memberId}', this.$route.params.id), { health_goal: snakecaseKeys(this.currentGoal) })
        .then((response: HealthGoalResponse) => {
          let goal = new HealthGoal(camelcaseKeys(response.health_goal, { deep: true }))
          goal.indicators = response.health_goal.indicators.map(indicator => new HealthIndicator(camelcaseKeys(indicator, { deep: true })))
          done(null, goal.indicators.length)
        })
        .catch((error) => console.log('cannot save goal', error))
    }
  }

  removeIndicators (done) {
    if (this.goal) {
      // remove all indicators from the previous goal
      this.goal!.indicators.map(indicator => {
        return this.apiClient.delete(API.TRAINERS_API.HEALTH_GOAL_INDICATORS.replace('{memberId}', this.$route.params.id) + '/' + indicator.id)
          .then(response => response)
          .catch(error => console.log('cannot save indicator', error))
      })
    }
    done(null)
  }

  /* If the newly created / updated goal has no indicators, submit the default ones */
  saveIndicators (existingIndicatorsCount, done?) {
    const defaultGoal = this.defaultGoals.filter(goal => goal.key === this.currentGoal.key)[0]
    if (!defaultGoal || defaultGoal.indicators.length === 0) {
      if (done) {
        done(null)
      }
      return undefined
    }

    // submit all the default indicators for the goal
    return Promise.all([defaultGoal.indicators.map(indicator => {
      // no need to submit an id
      delete (indicator as any).id
      // do not submit null sources, if these are not set
      if (!indicator.sourceId || !indicator.sourceType) {
        delete (indicator as any).sourceId
        delete (indicator as any).sourceType
      }
      return this.apiClient.post(API.TRAINERS_API.HEALTH_GOAL_INDICATORS.replace('{memberId}', this.$route.params.id), { indicator: snakecaseKeys(indicator) })
        .then(response => response)
        .catch(error => console.log('cannot save indicator', error))
    })])
      .then((response) => {
        if (done) {
          done(null)
        }
      })
  }

  loadCurrentGoal (done) {
    setTimeout(() => {
      return this.apiClient.get(API.TRAINERS_API.HEALTH_GOAL.replace('{memberId}', this.$route.params.id))
        .then((response: HealthGoalResponse) => {
          let goal = new HealthGoal(camelcaseKeys(response.health_goal, { deep: true }))
          goal.indicators = response.health_goal.indicators.map(indicator => new HealthIndicator(camelcaseKeys(indicator, { deep: true })))
          this.$store.dispatch('setGoal', goal)
          done(null)
        })
        .catch(error => console.log('cannot load health goal', error))
    }, 500)
  }

  removeGoal (done) {
    if (this.goal) {
      return this.apiClient.delete(API.TRAINERS_API.HEALTH_GOAL.replace('{memberId}', this.$route.params.id))
        .then(() => {
          done(null)
        })
        .catch((error) => console.log('cannot delete goal', error))
    } else done(null)
  }

  setCustomGoal (goalName: string) {
    // cleanup the select if typing a custom goal
    this.currentGoal = new HealthGoal({ name: goalName })

  }

  setTemplateGoal (goal: HealthGoal) {
    this.currentGoal = goal
  }

  close () {
    if (this.closeCallback) {
      this.closeCallback()
    }
  }

  render (h: CreateElement) {
    const predefinedGoals = this.defaultGoals.map((goal: HealthGoal) => {
      return <Option type='single-option' value={goal.key} label={this.$t(`PAGE.MEMBERS.GOALS.${goal.key}`)} selected={this.currentGoal.key === goal.key}
                     select={() => this.setTemplateGoal(goal)} />
    })

    return <Modal show={true} title={this.$t('PAGE.MEMBERS.GOALS.SET_MEMBER_GOAL')} closeCallback={this.close} cssClass={'add-goal'}>
      <div>
        <Subtitle1>{this.$t('PAGE.MEMBERS.GOALS.SET_MEMBER_GOAL_DESCRIPTION')}</Subtitle1>
        <Select ref='goalSelect' label={this.$t('PAGE.MEMBERS.GOALS.CHOOSE_GOAL')}
                value={this.currentGoal.isTemplateGoal ? this.$t(`PAGE.MEMBERS.GOALS.${this.currentGoal.key}`) : ''}>
          <OptionGroup>{predefinedGoals}</OptionGroup>
        </Select>

        <Subtitle1>{this.$t('PAGE.MEMBERS.GOALS.SET_CUSTOM_GOAL')}</Subtitle1>
        <Textarea ref='customGoal' value={this.currentGoal.name} label={this.$t('PAGE.MEMBERS.GOALS.CUSTOM_GOAL')} keyup={this.setCustomGoal} />
      </div>

      <div class='btn-row' slot='footer'>
        <Button ref='cancelBtn' type='outline' label={this.$t('CANCEL')} click={this.close} disabled={this.saveInProgress}/>
        <Button type='elevated' ref='saveBtn' label={this.$t('SAVE')} click={this.save}
                icon={this.saveInProgress ? '../img/loader' : ''}
                disabled={this.saveInProgress || (!this.currentGoal.name && !this.currentGoal.key)}/>
      </div>
    </Modal>
  }

  beforeMount () {
    if (this.goal) {
      this.currentGoal = this.goal
    }
  }
}
